import * as THREE from "three";
import { Enemy, LevelSim } from "@highmarch/domain";
import { groundAt } from "./terrain";
import { PooledVfx } from "./vfxPool";
import { enemyLook } from "./views/enemyLook";
import { reducedVfx } from "../ui/settings";

/** Portal surge at a wave's start: rings racing out, a flash, sparks rising; the camera gives a short kick. */
const SURGE = { rings: 3, ringSize: 1.6, ringStep: 1.1, ringLife: 0.55, flash: 1.6, sparkles: 26, shakeS: 0.35, shakeK: 0.12 } as const;
/** Every spawn: a small flash and ring in the unit's colour at the portal mouth. */
const SPAWN = { flash: 0.55, ring: 1.0, ringBig: 1.6 } as const;
/** Death ring size by kind of unit (world units): the bigger the thing that fell, the bigger the burst. */
const DEATH_RING = { unit: 1.1, big: 1.9, boss: 3.4 } as const;
/** A leak: a red flash and ring at the fortress, and a shake that says "that cost you". */
const LEAK = { flash: 1.4, ring: 2.6, shakeS: 0.3, shakeK: 0.16, gapS: 0.35 } as const;
const LEAK_COLOUR: [number, number, number] = [1, 0.25, 0.2];
const PORTAL_COLOUR: [number, number, number] = [0.72, 0.45, 1];

const rgb = (hex: number): [number, number, number] => [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];

/**
 * The wave's moments on the sprite VFX (art/vfx.js via vfxPool.ts): portal surge, emergence puffs, per-type death
 * bursts, fortress hits. Pure presentation driven by sim events; every effect is pooled sprites, no new meshes.
 */
export class WaveFx {
  private lastLeak = -1;
  private clock = 0;
  constructor(private readonly vfx: PooledVfx, private readonly sim: LevelSim, private readonly shake: (seconds: number, strength: number) => void) {}

  update(dt: number): void { this.clock += dt; }

  private portal(lift = 0.05): THREE.Vector3 { const p = this.sim.road.portal; return new THREE.Vector3(p.x, groundAt(p.x, p.y) + lift, p.y); }

  /**
   * The wave's start: the surge rings, sparkles and the camera kick. The portal's flash is the art's `vfx.waveStart`
   * flare, which the router plays on the same event, so there is no second flash here (the two used to stack).
   */
  stageStarted(): void {
    const at = this.portal();
    for (let i = 0; i < SURGE.rings; i++) this.vfx.ring(at, { size: SURGE.ringSize + i * SURGE.ringStep, life: SURGE.ringLife + i * 0.12, color: PORTAL_COLOUR });
    this.vfx.sparkles(at, SURGE.sparkles, { color: PORTAL_COLOUR, radius: 0.5, rise: 2.0, life: 1.1, size: 0.2 });
    if (!reducedVfx()) this.shake(SURGE.shakeS, SURGE.shakeK);
  }

  /** A surge or raid inside a wave: a smaller surge, no camera kick. */
  surge(): void {
    const at = this.portal();
    this.vfx.flash(at.clone().setY(at.y + 0.4), SURGE.flash * 0.7, PORTAL_COLOUR);
    for (let i = 0; i < 2; i++) this.vfx.ring(at, { size: SURGE.ringSize + i * SURGE.ringStep, life: SURGE.ringLife, color: PORTAL_COLOUR });
    this.vfx.sparkles(at, SURGE.sparkles / 2, { color: PORTAL_COLOUR, radius: 0.5, rise: 1.6, life: 0.9, size: 0.18 });
  }

  spawned(archetype: number, boss: boolean): void {
    const look = enemyLook(this.sim.balance.enemies[archetype], boss);
    const c = rgb(look.colour), at = this.portal();
    this.vfx.flash(at.clone().setY(at.y + 0.3), SPAWN.flash, c);
    this.vfx.ring(at, { size: look.role === "brute" || boss ? SPAWN.ringBig : SPAWN.ring, life: 0.35, color: c });
  }

  /** A kill's ring and sparks, at `where` (the body as drawn when it died: x, z) or else at the sim's spot for it. */
  killed(e: Pick<Enemy, "archetype" | "isBoss" | "x" | "y">, where?: THREE.Vector3): void {
    const look = enemyLook(this.sim.balance.enemies[e.archetype], e.isBoss);
    const c = rgb(look.colour);
    const x = where ? where.x : e.x.toNumber(), z = where ? where.z : e.y.toNumber();
    const at = new THREE.Vector3(x, groundAt(x, z) + 0.05, z);
    const kind = e.isBoss ? "boss" : look.role === "brute" ? "big" : "unit";
    this.vfx.ring(at, { size: DEATH_RING[kind], life: kind === "unit" ? 0.35 : 0.55, color: c });
    this.vfx.sparks(at.clone().setY(at.y + 0.25), kind === "unit" ? 8 : 16, { color: c, speed: kind === "unit" ? 2.4 : 3.4, life: 0.45, size: 0.14, floor: at.y });
    // no shake for a boss: its fall already carries trauma and a hit-stop (vfxRouter / vfx.bossFall), and a second
    // shake on top blurred that beat
  }

  leaked(): void {
    const b = this.sim.grid.base;
    const at = new THREE.Vector3(b.x, groundAt(b.x, b.y) + 0.05, b.y);
    this.vfx.flash(at.clone().setY(at.y + 0.8), LEAK.flash, LEAK_COLOUR);
    this.vfx.ring(at, { size: LEAK.ring, life: 0.5, color: LEAK_COLOUR });
    if (this.clock - this.lastLeak > LEAK.gapS && !reducedVfx()) { this.lastLeak = this.clock; this.shake(LEAK.shakeS, LEAK.shakeK); }
  }
}
